// RegisterPage.jsx â€” Self-service user registration
// Layout: shared with Loginform.jsx (split â€” dark gradient left + white form right)

import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users, TrendingUp, BarChart3, Shield,
  Eye, EyeOff, Check, X, CheckCircle2, Mail,
} from "lucide-react";

import ZenLogo from "../assets/images/ZEN LOGO-w 1.png";
import "../css/loginform.css";
import "../css/auth/authPages.css";

const API_URL = "https://apicrm.zenrealestate.in/api";

const features = [
  { icon: Users,      text: "Manage leads, agents, and site visits in one place" },
  { icon: TrendingUp, text: "Track deals from negotiation to closure" },
  { icon: BarChart3,  text: "Detailed MIS reports and performance analytics" },
  { icon: Shield,     text: "Role-based access for Super Admin, Manager & Agents" },
];

const validationSchema = Yup.object({
  Name:     Yup.string().min(2, "Name must be at least 2 characters").required("Name is required"),
  email:    Yup.string().email("Invalid email format").required("Email is required"),
  phone:    Yup.string().matches(/^[0-9]{0,15}$/, "Invalid phone number").nullable(),
  password: Yup.string()
    .min(8, "Min 8 characters")
    .matches(/[A-Z]/, "At least one uppercase letter")
    .matches(/[a-z]/, "At least one lowercase letter")
    .matches(/[0-9]/, "At least one number")
    .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "At least one special character")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

const passwordRules = (pw = "") => ([
  { label: "At least 8 characters",     ok: pw.length >= 8 },
  { label: "One uppercase letter",      ok: /[A-Z]/.test(pw) },
  { label: "One number",                ok: /[0-9]/.test(pw) },
  { label: "One special character",     ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw) },
]);

const passwordStrength = (pw = "") => {
  const rules  = passwordRules(pw);
  const passed = rules.filter(r => r.ok).length;
  if (!pw)         return { level: "",       label: "" };
  if (passed <= 1) return { level: "weak",   label: "Weak" };
  if (passed === 2)return { level: "fair",   label: "Fair" };
  if (passed === 3)return { level: "good",   label: "Good" };
  return                  { level: "strong", label: "Strong" };
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [showPw,   setShowPw]   = useState(false);
  const [showPw2,  setShowPw2]  = useState(false);
  const [success,  setSuccess]  = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const res = await axios.post(`${API_URL}/auth/register`, {
        Name:     values.Name.trim(),
        email:    values.email.trim().toLowerCase(),
        phone:    values.phone?.trim() || "",
        password: values.password,
      });
      toast.success(res.data.message || "Registration successful!", {
        position: "top-right", autoClose: 3000, theme: "colored",
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed", {
        position: "top-right", autoClose: 4002, theme: "colored",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-wrapper">
      {/* â”€â”€ Brand panel â”€â”€ */}
      <div className="login-left">
        <div className="login-brand">
          <img src={ZenLogo} alt="Zen Realty" className="login-brand-logo" />
          <h1>Join the<br/>ZenRealty Team</h1>
          <p>
            Get instant access to a powerful real-estate CRM designed to help you
            track leads, close deals, and grow revenue.
          </p>
        </div>

        <div className="login-features">
          {features.map(({ icon: Icon, text }, i) => (
            <div className="login-feature-item" key={i}>
              <div className="login-feature-icon"><Icon size={18} /></div>
              <span>{text}</span>
            </div>
          ))}
        </div>

        <p className="login-left-footer">
          Â© {new Date().getFullYear()} Zen Realty â€” Internal Use Only
        </p>
      </div>

      {/* â”€â”€ Registration form â”€â”€ */}
      <div className="login-right">
        <div className="login-form auth-form-wide">
          {success ? (
            // â”€â”€ Success state â”€â”€
            <>
              <div className="verify-status success">
                <div className="verify-status-icon">
                  <CheckCircle2 size={32} />
                </div>
                <h3>Account Created Successfully!</h3>
                <p>We've sent a verification link to your email. You can verify now or login and verify later.</p>
              </div>
              <button
                className="signIn-btn"
                onClick={() => navigate("/")}
              >
                Continue to Login
              </button>
            </>
          ) : (
            // â”€â”€ Form state â”€â”€
            <>
              <div className="login-form-header">
                <h2 className="welcome-text">Create Account</h2>
                <p className="subtitle">Get started with ZENREAL ESTATES CRM in seconds</p>
              </div>

              <Formik
                initialValues={{ Name: "", email: "", phone: "", password: "", confirmPassword: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values }) => {
                  const strength = passwordStrength(values.password);
                  const rules    = passwordRules(values.password);

                  return (
                    <Form>
                      <div className="auth-grid-2">
                        <div className="input-field">
                          <label htmlFor="Name">Full Name</label>
                          <Field id="Name" name="Name" type="text" placeholder="John Doe" />
                          <ErrorMessage name="Name" component="span" className="error-text" />
                        </div>
                        <div className="input-field">
                          <label htmlFor="phone">Phone (optional)</label>
                          <Field id="phone" name="phone" type="tel" placeholder="9876543210" />
                          <ErrorMessage name="phone" component="span" className="error-text" />
                        </div>
                      </div>

                      <div className="input-field">
                        <label htmlFor="email">Email Address</label>
                        <Field id="email" name="email" type="email" placeholder="you@zenrealty.co" />
                        <ErrorMessage name="email" component="span" className="error-text" />
                      </div>

                      <div className="input-field">
                        <label htmlFor="password">Password</label>
                        <div className="input-with-icon">
                          <Field id="password" name="password" type={showPw ? "text" : "password"} placeholder="Enter strong password" />
                          <button
                            type="button"
                            className="input-icon-btn"
                            onClick={() => setShowPw(v => !v)}
                            tabIndex={-1}
                          >
                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>

                        {values.password && (
                          <div className="password-strength">
                            <div className={`strength-bar ${strength.level}`}>
                              <div /><div /><div /><div />
                            </div>
                            {strength.label && (
                              <span className={`strength-label ${strength.level}`}>
                                {strength.label}
                              </span>
                            )}
                          </div>
                        )}

                        {values.password && (
                          <div className="password-rules">
                            {rules.map((r, i) => (
                              <div key={i} className={`password-rule ${r.ok ? "satisfied" : ""}`}>
                                {r.ok ? <Check size={12} /> : <X size={12} />}
                                <span>{r.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <ErrorMessage name="password" component="span" className="error-text" />
                      </div>

                      <div className="input-field">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <div className="input-with-icon">
                          <Field id="confirmPassword" name="confirmPassword" type={showPw2 ? "text" : "password"} placeholder="Re-enter password" />
                          <button
                            type="button"
                            className="input-icon-btn"
                            onClick={() => setShowPw2(v => !v)}
                            tabIndex={-1}
                          >
                            {showPw2 ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <ErrorMessage name="confirmPassword" component="span" className="error-text" />
                      </div>

                      <button type="submit" className="signIn-btn" disabled={isSubmitting}>
                        {isSubmitting ? "Creating accountâ€¦" : "Create Account"}
                      </button>
                    </Form>
                  );
                }}
              </Formik>

              <p className="footer-text">
                Already have an account?
                <Link to="/">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
